/**
 * PVReceptieGenerator - ANEXA 3: PV Recepție Finală
 * Bazat pe model HG 343/2017 - Proces Verbal de Recepție la Terminarea Lucrărilor
 */
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save, CheckCircle2, Plus, Trash2, UserPlus } from "lucide-react";

function generatePVReceptieHTML(d, h) {
  const membriRows = (d.membri_comisie || []).map((m, i) => `
    <tr>
      <td style="text-align:center">${i + 1}</td>
      <td>${m.name || "___"}</td>
      <td>${m.functie || "___"}</td>
      <td>${m.organizatie || "___"}</td>
    </tr>`).join("");

  return `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
    body{font-family:'Segoe UI',Arial,sans-serif;max-width:800px;margin:30px auto;padding:20px;color:#1e293b;font-size:12px;line-height:1.6}
    h1{text-align:center;color:#7c3aed;font-size:17px;margin-bottom:4px}
    h2{text-align:center;color:#8b5cf6;font-size:13px;font-weight:normal;margin-top:0}
    .header{border-bottom:3px solid #7c3aed;padding-bottom:12px;margin-bottom:18px}
    .section{margin-bottom:14px}
    .section-title{font-weight:bold;color:#7c3aed;border-bottom:1px solid #ede9fe;padding-bottom:3px;margin-bottom:8px;font-size:13px}
    table{width:100%;border-collapse:collapse;margin:8px 0}
    td,th{padding:5px 8px;border:1px solid #d1d5db;vertical-align:top;font-size:12px}
    th{background:#7c3aed;color:white;text-align:left;font-size:11px}
    .info-row td:first-child{width:40%;background:#f5f3ff;font-weight:600;color:#5b21b6}
    .check{color:#059669;font-weight:bold}
    .signatures{display:flex;justify-content:space-between;margin-top:35px;flex-wrap:wrap;gap:15px;page-break-inside:avoid}
    .sig-box{width:30%;text-align:center;border-top:2px solid #7c3aed;padding-top:8px}
    .sig-box p{margin:3px 0;font-size:11px}
    .footer{text-align:center;margin-top:25px;font-size:10px;color:#64748b;border-top:1px solid #e2e8f0;padding-top:8px}
    .note{background:#fef3c7;border:1px solid #fde68a;border-radius:6px;padding:10px;margin:10px 0;font-size:11px}
  </style></head><body>
  <div class="header">
    <h1>PROCES VERBAL DE RECEPȚIE LA TERMINAREA LUCRĂRILOR</h1>
    <h2>${(h && h.annex_label) || "ANEXA"} CTR ${d.contract_number || "___"} — PV Recepție Finală (Contract nr. ${d.contract_number || "___"} din ${d.contract_date || "___"})</h2>
    <p style="text-align:center;font-size:11px;color:#64748b">Întocmit conform HG 343/2017</p>
  </div>

  <div class="section">
    <div class="section-title">I. DATE GENERALE</div>
    <table>
      <tr class="info-row"><td>Obiectivul de investiție</td><td>${d.obiect_lucrare || "___"}</td></tr>
      <tr class="info-row"><td>Amplasament</td><td>${d.locatie_lucrare || "___"}</td></tr>
      <tr class="info-row"><td>Nr. contract / Data</td><td>Nr. ${d.contract_number || "___"} din ${d.contract_date || "___"}</td></tr>
      <tr class="info-row"><td>Executant</td><td>${d.executant_name || "___"}, CUI: ${d.executant_cui || "___"}</td></tr>
      <tr class="info-row"><td>Beneficiar / Investitor</td><td>${d.beneficiar_name || "___"}, CUI: ${d.beneficiar_cui || "___"}</td></tr>
      <tr class="info-row"><td>Valoare contract</td><td>${(d.pret_total || 0).toLocaleString("ro-RO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} lei</td></tr>
      <tr class="info-row"><td>Data începerii lucrărilor</td><td>${d.data_incepere || "___"}</td></tr>
      <tr class="info-row"><td>Data terminării lucrărilor</td><td>${d.data_terminare || "___"}</td></tr>
      <tr class="info-row"><td>Garanție acordată</td><td>${d.garantie_luni || "___"} luni</td></tr>
    </table>
  </div>

  <div class="section">
    <div class="section-title">II. COMISIA DE RECEPȚIE</div>
    ${membriRows ? `<table><thead><tr><th style="width:30px">Nr.</th><th>Nume și prenume</th><th>Funcția</th><th>Organizația</th></tr></thead><tbody>${membriRows}</tbody></table>` : "<p>Nu s-au specificat membrii comisiei.</p>"}
  </div>

  <div class="section">
    <div class="section-title">III. CONSTATĂRI</div>
    <p>${d.constatari || "Lucrările au fost executate conform contractului, proiectului tehnic și specificațiilor tehnice."}</p>
  </div>

  ${d.observatii_deficiente ? `<div class="section">
    <div class="section-title">IV. OBSERVAȚII / DEFICIENȚE CONSTATATE</div>
    <p>${d.observatii_deficiente}</p>
  </div>` : ""}

  ${d.remedieri ? `<div class="section">
    <div class="section-title">V. TERMEN DE REMEDIERE</div>
    <p>${d.remedieri}</p>
  </div>` : ""}

  <div class="section">
    <div class="section-title">${d.observatii_deficiente ? "VI" : "IV"}. CONCLUZIA COMISIEI</div>
    <p style="font-size:13px"><strong>${d.concluzie || "Comisia de recepție propune ADMITEREA recepției la terminarea lucrărilor."}</strong></p>
    ${d.concluzie_nota ? `<div class="note">${d.concluzie_nota}</div>` : ""}
  </div>

  <div class="signatures">
    <div class="sig-box">
      <p><strong>BENEFICIAR</strong></p>
      <p>${d.beneficiar_name || "___"}</p>
      <p>${d.beneficiar_reprezentant || "___"}</p>
      <p style="margin-top:25px">(semnătura și ștampila)</p>
    </div>
    <div class="sig-box">
      <p><strong>EXECUTANT</strong></p>
      <p>${d.executant_name || "___"}</p>
      <p>${d.executant_reprezentant || "___"}</p>
      <p style="margin-top:25px">(semnătura și ștampila)</p>
    </div>
    <div class="sig-box">
      <p><strong>PREȘEDINTE COMISIE</strong></p>
      <p>${(d.membri_comisie || [])[0]?.name || "___"}</p>
      <p style="margin-top:25px">(semnătura)</p>
    </div>
  </div>

  <div class="footer">
    <p>Prezentul proces verbal a fost încheiat în ${d.nr_exemplare || "3"} exemplare la data de ${d.data_receptie || new Date().toLocaleDateString("ro-RO")}.</p>
    <p>Document generat de <a href="https://e-tesa.ro" target="_blank" style="color: #1d4ed8; text-decoration: underline; font-weight: 500;">e-Tesa</a></p>
  </div>
  </body></html>`;
}

export default function PVReceptieGenerator({ contractData, existingAnnex, onSave, onCancel }) {
  const existing = existingAnnex?.data || {};

  const [form, setForm] = useState({
    contract_number: existing.contract_number || contractData.contract_number || "",
    contract_date: existing.contract_date || contractData.contract_date || "",
    executant_name: existing.executant_name || contractData.executant_name || "",
    executant_cui: existing.executant_cui || contractData.executant_cui || "",
    executant_reprezentant: existing.executant_reprezentant || contractData.executant_reprezentant || "",
    beneficiar_name: existing.beneficiar_name || contractData.beneficiar_name || "",
    beneficiar_cui: existing.beneficiar_cui || contractData.beneficiar_cui || "",
    beneficiar_reprezentant: existing.beneficiar_reprezentant || contractData.beneficiar_reprezentant || "",
    obiect_lucrare: existing.obiect_lucrare || contractData.obiect_lucrare || "",
    locatie_lucrare: existing.locatie_lucrare || contractData.locatie_lucrare || "",
    pret_total: existing.pret_total || contractData.pret_total || 0,
    garantie_luni: existing.garantie_luni || contractData.garantie_luni || "",
    data_incepere: existing.data_incepere || contractData.contract_start_date || "",
    data_terminare: existing.data_terminare || "",
    data_receptie: existing.data_receptie || new Date().toISOString().split("T")[0],
    nr_exemplare: existing.nr_exemplare || "3",
    membri_comisie: existing.membri_comisie || [
      { name: "", functie: "Președinte", organizatie: "" },
    ],
    constatari: existing.constatari || "Lucrările au fost executate conform contractului, proiectului tehnic și specificațiilor tehnice. Calitatea lucrărilor corespunde cerințelor din caietul de sarcini.",
    observatii_deficiente: existing.observatii_deficiente || "",
    remedieri: existing.remedieri || "",
    concluzie: existing.concluzie || "Comisia de recepție propune ADMITEREA recepției la terminarea lucrărilor.",
    concluzie_nota: existing.concluzie_nota || "",
  });

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const addMembru = () => setForm((p) => ({
    ...p,
    membri_comisie: [...p.membri_comisie, { name: "", functie: "Membru", organizatie: "" }],
  }));

  const removeMembru = (idx) => setForm((p) => ({
    ...p,
    membri_comisie: p.membri_comisie.filter((_, i) => i !== idx),
  }));

  const updateMembru = (idx, key, val) => {
    const next = [...form.membri_comisie];
    next[idx] = { ...next[idx], [key]: val };
    setForm((p) => ({ ...p, membri_comisie: next }));
  };

  const handleSave = () => {
    const header = { annex_label: existingAnnex?.annex_label || "ANEXA" };
    const html = generatePVReceptieHTML(form, header);
    onSave({
      annex_type: "pv_receptie",
      data: form,
      html_content: html,
      title: "PV Recepție Finală",
    });
  };

  return (
    <div className="space-y-4" data-testid="pv-receptie-editor">
      <Card className="border-0 shadow-lg border-t-4 border-t-violet-500">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onCancel} className="p-1 h-auto"><ArrowLeft className="w-5 h-5" /></Button>
            <CardTitle className="text-lg flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-violet-600" />
              {existingAnnex?.annex_label || "ANEXA"} — PV Recepție la Terminarea Lucrărilor
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date generale */}
          <div className="p-4 bg-violet-50 rounded-lg">
            <h3 className="font-semibold text-violet-800 mb-3">I. Date Generale</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Obiect lucrare</Label><Input value={form.obiect_lucrare} onChange={(e) => upd("obiect_lucrare", e.target.value)} /></div>
              <div className="col-span-2"><Label>Amplasament / Locație</Label><Input value={form.locatie_lucrare} onChange={(e) => upd("locatie_lucrare", e.target.value)} /></div>
              <div><Label>Nr. contract</Label><Input value={form.contract_number} onChange={(e) => upd("contract_number", e.target.value)} /></div>
              <div><Label>Data contract</Label><Input type="date" value={form.contract_date} onChange={(e) => upd("contract_date", e.target.value)} /></div>
              <div><Label>Valoare contract (lei)</Label><Input type="number" value={form.pret_total} onChange={(e) => upd("pret_total", parseFloat(e.target.value) || 0)} /></div>
              <div><Label>Garanție (luni)</Label><Input type="number" value={form.garantie_luni} onChange={(e) => upd("garantie_luni", e.target.value)} /></div>
              <div><Label>Data începerii</Label><Input type="date" value={form.data_incepere} onChange={(e) => upd("data_incepere", e.target.value)} /></div>
              <div><Label>Data terminării</Label><Input type="date" value={form.data_terminare} onChange={(e) => upd("data_terminare", e.target.value)} /></div>
              <div><Label>Data recepției</Label><Input type="date" value={form.data_receptie} onChange={(e) => upd("data_receptie", e.target.value)} /></div>
              <div><Label>Nr. exemplare</Label><Input value={form.nr_exemplare} onChange={(e) => upd("nr_exemplare", e.target.value)} /></div>
            </div>
          </div>

          {/* Părți */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <h4 className="font-semibold text-slate-700">Executant</h4>
              <div><Label>Firmă</Label><Input value={form.executant_name} onChange={(e) => upd("executant_name", e.target.value)} /></div>
              <div><Label>CUI</Label><Input value={form.executant_cui} onChange={(e) => upd("executant_cui", e.target.value)} /></div>
              <div><Label>Reprezentant</Label><Input value={form.executant_reprezentant} onChange={(e) => upd("executant_reprezentant", e.target.value)} /></div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg space-y-2">
              <h4 className="font-semibold text-slate-700">Beneficiar</h4>
              <div><Label>Firmă</Label><Input value={form.beneficiar_name} onChange={(e) => upd("beneficiar_name", e.target.value)} /></div>
              <div><Label>CUI</Label><Input value={form.beneficiar_cui} onChange={(e) => upd("beneficiar_cui", e.target.value)} /></div>
              <div><Label>Reprezentant</Label><Input value={form.beneficiar_reprezentant} onChange={(e) => upd("beneficiar_reprezentant", e.target.value)} /></div>
            </div>
          </div>

          {/* Comisia de recepție */}
          <div className="p-4 bg-violet-50/50 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-violet-800">II. Comisia de Recepție</h3>
              <Button variant="outline" size="sm" onClick={addMembru} className="border-violet-300 text-violet-600" data-testid="add-comisie-member">
                <UserPlus className="w-4 h-4 mr-1" /> Adaugă Membru
              </Button>
            </div>
            <div className="space-y-2">
              {form.membri_comisie.map((m, idx) => (
                <div key={m.name ? `pv-${m.name}-${idx}` : `pv-${idx}`} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-4"><Label>Nume</Label><Input value={m.name} onChange={(e) => updateMembru(idx, "name", e.target.value)} placeholder="Nume complet" /></div>
                  <div className="col-span-3"><Label>Funcție</Label><Input value={m.functie} onChange={(e) => updateMembru(idx, "functie", e.target.value)} /></div>
                  <div className="col-span-4"><Label>Organizație</Label><Input value={m.organizatie} onChange={(e) => updateMembru(idx, "organizatie", e.target.value)} /></div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="sm" onClick={() => removeMembru(idx)} className="text-red-400 hover:text-red-600 h-9 w-9 p-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Constatări */}
          <div>
            <Label className="font-semibold">III. Constatări</Label>
            <Textarea value={form.constatari} onChange={(e) => upd("constatari", e.target.value)} rows={3} className="mt-1" />
          </div>

          <div>
            <Label className="font-semibold">IV. Observații / Deficiențe constatate</Label>
            <Textarea value={form.observatii_deficiente} onChange={(e) => upd("observatii_deficiente", e.target.value)} rows={2} className="mt-1" placeholder="Opțional — lăsați gol dacă nu sunt deficiențe" />
          </div>

          {form.observatii_deficiente && (
            <div>
              <Label className="font-semibold">V. Termene remediere</Label>
              <Textarea value={form.remedieri} onChange={(e) => upd("remedieri", e.target.value)} rows={2} className="mt-1" placeholder="Specificați termenele de remediere..." />
            </div>
          )}

          <div>
            <Label className="font-semibold">Concluzia Comisiei</Label>
            <Textarea value={form.concluzie} onChange={(e) => upd("concluzie", e.target.value)} rows={2} className="mt-1" />
          </div>

          <div>
            <Label>Notă suplimentară (opțional)</Label>
            <Textarea value={form.concluzie_nota} onChange={(e) => upd("concluzie_nota", e.target.value)} rows={2} placeholder="Mențiuni suplimentare..." />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button variant="outline" onClick={onCancel}>Anulează</Button>
            <Button onClick={handleSave} className="bg-violet-600 hover:bg-violet-700" data-testid="save-pv-receptie">
              <Save className="w-4 h-4 mr-2" />Salvează {existingAnnex?.annex_label || "ANEXA"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
